import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Stack,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// utils
import { compressImage } from '../../../utils/imageCompression';
// components
import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
import tracker from '../../../api/tracker';
import RHFFileInput from '../../../components/hook-form/RHFFileInput';
import Label from '../../../components/Label';
import EventPhotos from './EventPhotos';
import Iconify from '../../../components/Iconify';
// ----------------------------------------------------------------------

const steps = ['Event Details', 'Photos & Videos'];

export default function EventForm({ event, isEdit = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [error, setError] = useState(null);
  const [openActivateDialog, setOpenActivateDialog] = useState(false);
  const [openNoPhotosDialog, setOpenNoPhotosDialog] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const EventSchema = Yup.object().shape({
    name: Yup.string().required(t('Required')),
    description: Yup.string().required(t('Required')),
    date: Yup.date().required(t('Required')).typeError(t('Invalid date')),
    date_end: Yup.date()
      .required(t('Required'))
      .typeError(t('Invalid date'))
      .min(Yup.ref('date'), t('End date must be after start date')),
    image: isEdit ? Yup.mixed().nullable() : Yup.mixed().required(t('Required')),
  });

  const defaultValues = {
    name: event?.name || '',
    description: event?.description || '',
    date: event?.date ? moment(event.date, 'YYYY-MM-DD') : null,
    date_end: event?.date_end ? moment(event.date_end, 'YYYY-MM-DD') : null,
    image: null,
    active: isEdit ? event?.active ?? true : false,
  };

  const methods = useForm({
    resolver: yupResolver(EventSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { isSubmitting, isDirty, dirtyFields, errors },
  } = methods;

  useEffect(() => {
    if (event && isEdit) {
      setValue('name', event.name || '', { shouldDirty: false });
      setValue('description', event.description || '', { shouldDirty: false });
      setValue('active', event.active ?? true, { shouldDirty: false });
      setValue('date', event.date ? moment(event.date, 'YYYY-MM-DD') : null, { shouldDirty: false });
      setValue('date_end', event.date_end ? moment(event.date_end, 'YYYY-MM-DD') : null, { shouldDirty: false });
    }
  }, [event, isEdit, setValue]);

  const handleCreateEvent = async (data) => {
    let compressedImage = null;
    if (data.image && data.image[0]) {
      try {
        compressedImage = await compressImage(data.image[0], {
          maxSizeMB: 5,
          maxWidthOrHeight: 1200,
        });
        data.image = compressedImage;
      } catch (error) {
        setError(t('Failed to compress image'));
        return;
      }
    }

    data.date = data.date ? moment(data.date).format('YYYY-MM-DD') : '';
    data.date_end = data.date_end ? moment(data.date_end).format('YYYY-MM-DD') : '';
    data.active = false;

    try {
      const response = await tracker.post('events', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const eventId = response.data.data?.hashid;
      if (!eventId) throw new Error('No hashid returned from API');
      setCreatedEventId(eventId);
      setActiveStep(1);
    } catch (error) {
      setError(t('Failed to create event'));
    }
  };

  const handleEditEvent = async (data) => {
    let compressedImage = null;
    if (data.image && data.image[0]) {
      try {
        compressedImage = await compressImage(data.image[0], {
          maxSizeMB: 5,
          maxWidthOrHeight: 1200,
        });
        data.image = compressedImage;
      } catch (error) {
        setError(t('Failed to compress image'));
        return;
      }
    }

    data.date = data.date ? moment(data.date).format('YYYY-MM-DD') : '';
    data.date_end = data.date_end ? moment(data.date_end).format('YYYY-MM-DD') : '';

    const updatedData = {
      name: data.name,
      description: data.description,
      date: data.date,
      date_end: data.date_end,
    };
    if (compressedImage) {
      updatedData.image = compressedImage;
    }
    if (dirtyFields.active) {
      updatedData.active = data.active;
    }

    try {
      await tracker.post(`events/update/${event.hashid}`, updatedData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/dashboard/events', { replace: true });
    } catch (error) {
      setError(
        error.response?.status === 422 ? t('Cannot activate event without photos') : t('Failed to update event')
      );
    }
  };

  const handleDeleteEvent = async () => {
    setDeleting(true);
    try {
      await tracker.delete(`/events/destroy/${event.hashid}`);
      setOpenDeleteDialog(false);
      setError(null);
      navigate('/dashboard/events', { replace: true, state: { message: t('Event deleted successfully') } });
    } catch (error) {
      console.error('Error deleting event:', error);
      setError(t('Failed to delete event'));
    } finally {
      setDeleting(false);
    }
  };

  const handleFinish = async () => {
    if (createdEventId) {
      const hasPhotos = await checkEventPhotos(createdEventId);
      const finalHasPhotos = hasPhotos || photoCount > 0;
      if (finalHasPhotos) {
        setOpenActivateDialog(true);
      } else {
        setOpenNoPhotosDialog(true);
      }
    } else {
      navigate('/dashboard/events', { replace: true });
    }
  };

  const handleActivateConfirm = async (activate) => {
    setOpenActivateDialog(false);
    if (activate && createdEventId) {
      try {
        await tracker.post(
          `events/activate/${createdEventId}`,
          { active: true },
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
      } catch (error) {
        setError(t('Cannot activate event without photos'));
        return;
      }
    }
    navigate('/dashboard/events', { replace: true });
  };

  const handleNoPhotosClose = () => {
    setOpenNoPhotosDialog(false);
    navigate('/dashboard/events', { replace: true });
  };

  const checkEventPhotos = async (hashid) => {
    try {
      const response = await tracker.get(`events/${hashid}`);
      const photos = response.data.data?.assets || response.data.data?.photos || [];
      return photos.length > 0;
    } catch (error) {
      return false;
    }
  };

  const handlePhotoCountUpdate = (count) => {
    setPhotoCount(count);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const nameValue = watch('name');
  const activeValue = watch('active');

  const renderFormFields = () => (
    <Stack spacing={3}>
      {error && <Typography color="error">{error}</Typography>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <RHFTextField
          name="name"
          label={t('Name')}
          inputProps={{ maxLength: 40 }}
          helperText={`${nameValue?.length || 0}/40`}
        />
      </Stack>
      <RHFTextField name="description" label={t('Description')} />
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Controller
          name="date"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DesktopDatePicker
              label={t('Date_event')}
              inputFormat="MM/DD/YYYY"
              value={field.value}
              onChange={(newValue) => field.onChange(newValue)}
              renderInput={(params) => (
                <TextField {...params} error={!!error} helperText={error ? error.message : null} />
              )}
            />
          )}
        />
        <Controller
          name="date_end"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DesktopDatePicker
              label={t('end_date')}
              inputFormat="MM/DD/YYYY"
              value={field.value}
              onChange={(newValue) => field.onChange(newValue)}
              renderInput={(params) => (
                <TextField {...params} error={!!error} helperText={error ? error.message : null} />
              )}
            />
          )}
        />
      </LocalizationProvider>
      <div style={{ marginTop: '80px' }}>
        <Label>{t('Image')}</Label>
        <RHFFileInput
          name="image"
          fileUrl={event?.image || null}
          error={!!errors.image}
          helperText={errors.image?.message}
        />
      </div>
      {isEdit && <RHFCheckbox name="active" label={t('Activo')} />}
      {isEdit && (
        <Typography variant="caption">
          Active: {activeValue ? 'Yes' : 'No'} (Changed: {dirtyFields.active ? 'Yes' : 'No'})
        </Typography>
      )}
      {!isEdit && (
        <Typography variant="caption" color="textSecondary">
          {t('Event activation available after adding photos')}
        </Typography>
      )}
    </Stack>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderFormFields();
      case 1:
        return createdEventId ? (
          <EventPhotos event={{ hashid: createdEventId }} onPhotoCountUpdate={handlePhotoCountUpdate} />
        ) : (
          <Typography color="error">{t('No event ID available')}</Typography>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <FormProvider methods={methods}>
      <Box sx={{ width: '100%' }}>
        {isEdit ? (
          <>
            {renderFormFields()}
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                loading={isSubmitting}
                onClick={handleSubmit(handleEditEvent)}
              >
                {t('Save')}
              </LoadingButton>
              <LoadingButton
                fullWidth
                size="large"
                variant="contained"
                color="error"
                loading={deleting}
                onClick={() => setOpenDeleteDialog(true)}
                startIcon={<Iconify icon="quill:folder-trash" />}
              >
                {t('Delete Event')}
              </LoadingButton>
            </Stack>
          </>
        ) : (
          <>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{t(label)}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ mt: 3 }}>
              {getStepContent(activeStep)}
              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <LoadingButton fullWidth variant="contained" disabled={activeStep === 0} onClick={handleBack}>
                  {t('Back')}
                </LoadingButton>
                {activeStep === 0 ? (
                  <LoadingButton
                    fullWidth
                    variant="contained"
                    loading={isSubmitting}
                    onClick={handleSubmit(handleCreateEvent)}
                  >
                    {t('Next')}
                  </LoadingButton>
                ) : (
                  <LoadingButton fullWidth size="large" variant="contained" onClick={handleFinish}>
                    {t('Finish')}
                  </LoadingButton>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Box>

      {/* Activation Dialog */}
      <Dialog open={openActivateDialog} onClose={() => handleActivateConfirm(false)}>
        <DialogTitle>{t('Activate Event')}</DialogTitle>
        <DialogContent>
          <Typography>{t('Do you want to activate this event now?')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleActivateConfirm(false)} color="primary">
            {t('No')}
          </Button>
          <Button onClick={() => handleActivateConfirm(true)} color="primary" variant="contained">
            {t('Yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* No Photos Dialog */}
      <Dialog open={openNoPhotosDialog} onClose={handleNoPhotosClose}>
        <DialogTitle>{t('Cannot Activate Event')}</DialogTitle>
        <DialogContent>
          <Typography>{t('Event cannot be activated without photos')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoPhotosClose} color="primary" variant="contained">
            {t('OK')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('Delete Event')}</DialogTitle>
        <DialogContent>
          <Typography>{t('Are you sure you want to delete this event? This action cannot be undone.')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            {t('Cancel')}
          </Button>
          <LoadingButton onClick={handleDeleteEvent} color="error" variant="contained" loading={deleting}>
            {t('Delete')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
